import { createElement, useMemo, type ReactNode } from 'react';
import { parseProse, type ProseNode } from '@/lib/prose';

interface SafeRichTextProps {
  content: string;
  className?: string;
}

function renderNodes(nodes: ProseNode[]): ReactNode[] {
  return nodes.map((node, key) => {
    if (typeof node === 'string') return node;
    const props: Record<string, unknown> = { key };
    if (node.textAlign) props.style = { textAlign: node.textAlign };
    if (node.tag === 'ol' && node.start !== undefined) props.start = node.start;
    if (node.tag === 'a' && node.href) {
      props.href = node.href;
      props.target = '_blank';
      props.rel = 'noopener noreferrer nofollow ugc';
    }
    // Invalid links become noninteractive text; no source attributes are spread.
    const tag = node.tag === 'a' && !node.href ? 'span' : node.tag;
    if (tag === 'br' || tag === 'hr') return createElement(tag, props);
    return createElement(tag, props, ...renderNodes(node.children));
  });
}

export function SafeRichText({ content, className = '' }: SafeRichTextProps) {
  const parsed = useMemo(() => parseProse(content), [content]);
  return (
    <div className={`prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap break-words leading-relaxed ${className}`}>
      {renderNodes(parsed.nodes)}
    </div>
  );
}
